import React, { useEffect, useRef, useState } from 'react';
import { FaAward, FaBook, FaChalkboard, FaCube, FaEdit, FaGraduationCap, FaHeadset, FaHeart, FaLaptop, FaMap, FaPencilAlt, FaSignOutAlt, FaPlayCircle, FaRegQuestionCircle, FaRegUser, FaSearch, FaStar, FaTag, FaUser, FaUserTie, FaWrench, FaComments, FaClipboardList, FaFileAlt, FaListAlt, FaCogs, FaVideo, FaShoppingCart, FaUsers, FaQuestion, FaPhone, FaCalendarAlt } from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './Navbar.module.scss';
import { searchMessages } from '../data/homePageTagLinesData';
import { MenuItem } from '../data/menuItem';
import { getDashboardMenu } from '../services/DashboardService';
import CallbackForm from './CallbackForm';

// Map icon names to Font Awesome components, or infer from menu item text
const getIconComponent = (iconName: string | undefined, itemText?: string) => {
  // Check if backend provided an icon name
  const iconMap: { [key: string]: JSX.Element } = {
    'ClipboardList': <FaClipboardList aria-hidden="true" />,
    'FileAlt': <FaFileAlt aria-hidden="true" />,
    'ListAlt': <FaListAlt aria-hidden="true" />,
    'Cogs': <FaCogs aria-hidden="true" />,
    'Settings': <FaCogs aria-hidden="true" />,
    'Dashboard': <FaClipboardList aria-hidden="true" />,
  };
  
  if (iconName && iconMap[iconName]) {
    return iconMap[iconName];
  }
  
  // Infer icon based on menu item text if no icon provided
  if (itemText) {
    const text = itemText.toLowerCase();
    
    if (text.includes('dashboard')) return <FaClipboardList aria-hidden="true" />;
    if (text.includes('tutorial')) return <FaVideo aria-hidden="true" />;
    if (text.includes('course')) return <FaBook aria-hidden="true" />;
    if (text.includes('order')) return <FaShoppingCart aria-hidden="true" />;
    if (text.includes('student')) return <FaUsers aria-hidden="true" />;
    if (text.includes('mentor')) return <FaUserTie aria-hidden="true" />;
    if (text.includes('inquiry')) return <FaQuestion aria-hidden="true" />;
    if (text.includes('request')) return <FaPhone aria-hidden="true" />;
    if (text.includes('interview')) return <FaComments aria-hidden="true" />;
    if (text.includes('life skill')) return <FaHeart aria-hidden="true" />;
    if (text.includes('booking')) return <FaCalendarAlt aria-hidden="true" />;
    if (text.includes('government') || text.includes('scheme')) return <FaBook aria-hidden="true" />;
  }
  
  // Default fallback
  return <FaClipboardList aria-hidden="true" />;
};

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const { isAuthenticated } = useAuth();
  const [mentorMessage, setMentorMessage] = useState<string>('Search for Courses');
  const navigate = useNavigate();
  const { logout } = useAuth();
  const location = useLocation();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const defaultClaim = localStorage.getItem('defaultClaim') || '';

  const [showModal, setShowModal] = useState<boolean>(false);
  const handleFormSubmit = (formData: { name: string; mobile: string; email: string; message: string }) => {
    console.log('Form submitted:', formData);
    setShowModal(false); // Close the modal after submission
  };
  const handleClick = () => {
    setShowModal(true);
  };
  const getRandomTagLine = () => {
    const banners = searchMessages;
    const randomIndex = Math.floor(Math.random() * banners.length);
    return banners[randomIndex];
  };

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const data = await getDashboardMenu();
        setMenuItems(data);
      } catch (error) {
        console.error("Error fetching menu items:", error);
      }
    };
    fetchMenuItems();
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setMentorMessage(getRandomTagLine());
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        toggleRef.current &&
        !toggleRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(prev => !prev);
  };
  const navigateToSearch = () => {
    navigate("/search/")
  }
  const handleLogout = () => {
    logout();
  };

  const logoSrc =
    localStorage.getItem('theme') === 'light'
      ? 'https://images.courseecho.com/light-logo.webp'
      : 'https://images.courseecho.com/dark-logo.webp';

  return (
    <>
      {/* ── Navbar ── */}
      <header role="banner">
        <nav className={styles.navbar} aria-label="Main navigation">
          <div className={styles.navInner}>

            {/* Logo */}
            <Link className={styles.logoLink} to="/" aria-label="CourseEcho Home">
              <img
                src={logoSrc}
                height={32}
                width={32}
                alt="CourseEcho Logo"
                className={styles.logoImg}
              />
              <span className={styles.logoText}>CourseEcho</span>
            </Link>

            {/* Search bar */}
            <div
              className={styles.searchBar}
              role="search"
              aria-label="Search courses and mentors"
              onClick={navigateToSearch}
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && navigateToSearch()}
            >
              <FaSearch className={styles.searchIcon} aria-hidden="true" />
              <span className={styles.searchText}>{mentorMessage}</span>
            </div>

            {/* Right actions */}
            <div className={styles.navRight}>

              {/* Help */}
              <button
                className={styles.helpBtn}
                title="Need help?"
                aria-label="Open help"
                onClick={handleClick}
              >
                <FaRegQuestionCircle aria-hidden="true" />
              </button>

              {/* Sign up (unauthenticated only) */}
              {!isAuthenticated && (
                <Link to="/signup/" className={styles.signupBtn} aria-label="Create a free account">
                  <FaRegUser aria-hidden="true" />
                  Sign up
                </Link>
              )}

              {/* Menu toggle */}
              <button
                ref={toggleRef}
                className={`${styles.menuToggle} ${isOpen ? styles.menuToggleOpen : ''}`}
                onClick={toggleMenu}
                aria-expanded={isOpen}
                aria-controls="site-menu"
                aria-label={isOpen ? 'Close menu' : 'Open menu'}
              >
                <span>Menu</span>
                <div className={styles.hamburger} aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* ── Dropdown overlay + panel ── */}
      {isOpen && (
        <>
          <div
            className={styles.dropdownBackdrop}
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          <nav
            id="site-menu"
            ref={dropdownRef}
            className={styles.dropdown}
            aria-label="Site navigation menu"
          >
            <div className={styles.dropdownInner}>

              {isAuthenticated ? (
                <>
                  <p className={styles.menuWelcome}>Welcome Back 👋</p>

                  {/* Products */}
                  <div className={styles.menuSection}>
                    <p className={styles.menuSectionLabel}>Products &amp; Solutions</p>
                  </div>
                  <Link to="/products/" className={styles.menuItem} onClick={() => setIsOpen(false)}>
                    <FaCube aria-hidden="true" /> AI Widget SDK
                  </Link>
                  <Link to="/docs/" className={styles.menuItem} onClick={() => setIsOpen(false)}>
                    <FaBook aria-hidden="true" /> Documentation
                  </Link>
                  <Link to="/pricing/" className={styles.menuItem} onClick={() => setIsOpen(false)}>
                    <FaTag aria-hidden="true" /> Pricing
                  </Link>
                  <Link to="/tools/ai-communication-lab/" className={styles.menuItem} onClick={() => setIsOpen(false)}>
                    <FaComments aria-hidden="true" /> AI Communication Lab
                  </Link>

                  <hr className={styles.separator} />

                  {/* Learning */}
                  <div className={styles.menuSection}>
                    <p className={styles.menuSectionLabel}>Learning Path</p>
                  </div>
                  <Link to="/career-counseling/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaHeadset aria-hidden="true" /> Career Counseling (1-to-1)</Link>
                  <Link to="/courses/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaGraduationCap aria-hidden="true" /> Courses</Link>
                  <Link to="/life-skills/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaHeart aria-hidden="true" /> Life Skills</Link>
                  <Link to="/government-schemes/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaBook aria-hidden="true" /> Government Schemes</Link>
                  <Link to="/scholarships/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaAward aria-hidden="true" /> All Scholarships</Link>
                  <Link to="/tools/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaWrench aria-hidden="true" /> Free AI Tools</Link>
                  <Link to="/tutorials/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaPlayCircle aria-hidden="true" /> Tutorials</Link>
                  <Link to="/interviews/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaComments aria-hidden="true" /> Interviews</Link>
                  <Link to="/campus-counselor/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaChalkboard aria-hidden="true" /> Campus Counselor</Link>
                  <Link to="/resources/roadmap/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaMap aria-hidden="true" /> Career Roadmaps</Link>
                  <Link to="/services/project/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaLaptop aria-hidden="true" /> Project Development Support</Link>
                  <Link to="/mentors/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaUserTie aria-hidden="true" /> Meet Mentors</Link>

                  <hr className={styles.separator} />

                  {/* Content */}
                  <div className={styles.menuSection}>
                    <p className={styles.menuSectionLabel}>Content</p>
                  </div>
                  <Link to="/blog/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaPencilAlt aria-hidden="true" /> Blog</Link>
                  <Link to="/blog/write/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaEdit aria-hidden="true" /> Write for Us</Link>

                  <hr className={styles.separator} />

                  {/* Dashboard items from API */}
                  {menuItems.length > 0 && (
                    <>
                      <div className={styles.menuSection}>
                        <p className={styles.menuSectionLabel}>My Dashboard</p>
                      </div>
                      {menuItems.map((item) => (
                        <Link
                          key={item.id}
                          to={item.url}
                          className={styles.menuItem}
                          onClick={() => setIsOpen(false)}
                        >
                          {getIconComponent(item.icon, item.text)} {item.text}
                        </Link>
                      ))}
                      <hr className={styles.separator} />
                    </>
                  )}

                  {(defaultClaim === 'SUPERADMIN' || defaultClaim === 'ADMIN') && (
                    <>
                      <div className={styles.menuSection}>
                        <p className={styles.menuSectionLabel}>Admin Content</p>
                      </div>
                      <Link to="/dashboard/government-schemes/" className={styles.menuItem} onClick={() => setIsOpen(false)}>
                        <FaBook aria-hidden="true" /> Government Schemes
                      </Link>
                      <hr className={styles.separator} />
                    </>
                  )}

                  <Link to="/profile/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaUser aria-hidden="true" /> Profile</Link>
                  <button className={styles.menuItem} onClick={handleLogout}><FaSignOutAlt aria-hidden="true" /> Logout</button>
                </>
              ) : (
                <>
                  {/* Products */}
                  <div className={styles.menuSection}>
                    <p className={styles.menuSectionLabel}>Products &amp; Solutions</p>
                  </div>
                  <Link to="/products/" className={styles.menuItem} onClick={() => setIsOpen(false)}>
                    <FaCube aria-hidden="true" /> AI Widget SDK
                  </Link>
                  <Link to="/docs/" className={styles.menuItem} onClick={() => setIsOpen(false)}>
                    <FaBook aria-hidden="true" /> Documentation
                  </Link>
                  <Link to="/pricing/" className={`${styles.menuItem} ${styles.menuItemHighlight}`} onClick={() => setIsOpen(false)}>
                    <FaTag aria-hidden="true" /> Pricing
                  </Link>
                  <Link to="/tools/ai-communication-lab/" className={styles.menuItem} onClick={() => setIsOpen(false)}>
                    <FaComments aria-hidden="true" /> AI Communication Lab
                  </Link>

                  <hr className={styles.separator} />

                  {/* Students */}
                  <div className={styles.menuSection}>
                    <p className={styles.menuSectionLabel}>For Students</p>
                  </div>
                  <Link to="/career-counseling/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaHeadset aria-hidden="true" /> Career Counseling (1-to-1)</Link>
                  <Link to="/courses/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaGraduationCap aria-hidden="true" /> Courses</Link>
                  <Link to="/life-skills/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaHeart aria-hidden="true" /> Life Skills</Link>
                  <Link to="/government-schemes/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaBook aria-hidden="true" /> Government Schemes</Link>
                  <Link to="/scholarships/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaAward aria-hidden="true" /> All Scholarships</Link>
                  <Link to="/scholarships-for-engineering-students/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaAward aria-hidden="true" /> Engineering Scholarships</Link>
                  <Link to="/tools/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaWrench aria-hidden="true" /> Free AI Tools</Link>
                  <Link to="/tutorials/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaPlayCircle aria-hidden="true" /> Tutorials</Link>
                  <Link to="/resources/roadmap/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaMap aria-hidden="true" /> Career Roadmaps</Link>
                  <Link to="/services/project/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaLaptop aria-hidden="true" /> Project Development Support</Link>
                  <Link to="/interviews/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaComments aria-hidden="true" /> Interviews</Link>
                  <Link to="/campus-counselor/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaChalkboard aria-hidden="true" /> Campus Counselor</Link>

                  <hr className={styles.separator} />

                  {/* Experts */}
                  <div className={styles.menuSection}>
                    <p className={styles.menuSectionLabel}>For Experts</p>
                  </div>
                  <Link to="/mentor/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaStar aria-hidden="true" /> Become a Mentor</Link>
                  <Link to="/mentors/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaUserTie aria-hidden="true" /> Find Expert Mentors</Link>
                  <Link to="/become-a-campus-counselor/" className={styles.menuItem} onClick={() => setIsOpen(false)}><FaGraduationCap aria-hidden="true" /> Become a Counselor</Link>

                  <hr className={styles.separator} />

                  {/* Auth */}
                  <div className={styles.authActions}>
                    <Link to="/login/" className={styles.loginLink} onClick={() => setIsOpen(false)}>Log in</Link>
                    <Link to="/signup/" className={styles.signupLink} onClick={() => setIsOpen(false)}>Sign up free</Link>
                  </div>
                </>
              )}
            </div>
          </nav>
        </>
      )}

      {showModal && (
        <CallbackForm
          onClose={() => setShowModal(false)}
          onSubmit={handleFormSubmit}
        />
      )}
    </>
  );
};

export default Navbar;
